/**
 * Events Context Provider
 *
 * Initializes the Event SDK and provides it to the application.
 * Should be placed near the root of the component tree.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  initializeEventSDK,
  updateEventSDKConfig,
  shutdownEventSDK,
  getSDKStatus,
  forceSyncNow,
  type ConsentScope,
} from '../lib/events';

// ============================================
// TYPES
// ============================================

interface EventsConfig {
  tenantId: string;
  userId?: string;
  consentScope?: ConsentScope;
  syncEndpoint?: string;
  enabled?: boolean;
}

interface EventsContextValue {
  /** Whether the SDK is initialized */
  initialized: boolean;

  /** Whether the SDK is enabled */
  enabled: boolean;

  /** Whether we're online */
  online: boolean;

  /** Current tenant ID */
  tenantId: string | null;

  /** Update user ID (e.g., after login) */
  setUserId: (userId: string) => void;

  /** Update consent scope */
  setConsentScope: (scope: ConsentScope) => void;

  /** Force immediate sync */
  forceSync: () => Promise<{ synced: number; failed: number }>;

  /** Get SDK status */
  getStatus: () => Promise<Awaited<ReturnType<typeof getSDKStatus>>>;
}

// ============================================
// CONTEXT
// ============================================

const EventsContext = createContext<EventsContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface EventsProviderProps {
  children: ReactNode;
  config: EventsConfig;
}

/**
 * Provider that initializes and manages the Event SDK
 */
export function EventsProvider({
  children,
  config,
}: EventsProviderProps): JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Initialize SDK on mount
  useEffect(() => {
    initializeEventSDK({
      tenantId: config.tenantId,
      userId: config.userId,
      defaultConsentScope: config.consentScope || 'personal_only',
      syncEndpoint: config.syncEndpoint,
      enabled: config.enabled ?? true,
    });

    setInitialized(true);

    // Listen for online/offline
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      shutdownEventSDK();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [config.tenantId]);

  // Update config when props change
  useEffect(() => {
    if (initialized) {
      updateEventSDKConfig({
        userId: config.userId,
        defaultConsentScope: config.consentScope || 'personal_only',
        enabled: config.enabled ?? true,
      });
    }
  }, [initialized, config.userId, config.consentScope, config.enabled]);

  // Context value
  const value: EventsContextValue = {
    initialized,
    enabled: config.enabled ?? true,
    online,
    tenantId: config.tenantId,

    setUserId: (userId: string) => {
      updateEventSDKConfig({ userId });
    },

    setConsentScope: (scope: ConsentScope) => {
      updateEventSDKConfig({ defaultConsentScope: scope });
    },

    forceSync: forceSyncNow,
    getStatus: getSDKStatus,
  };

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

/**
 * Access the Events context
 */
export function useEventsContext(): EventsContextValue {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEventsContext must be used within EventsProvider');
  }
  return context;
}

// ============================================
